/**
 * Ensures the destination route requires an authenticated Shopware customer
 * session (`useUser().isLoggedIn`).
 *
 * Flow:
 * - If the Shopware user is already logged in, continue navigation.
 * - Otherwise force a Shopware session-context sync once.
 * - If login is still missing, attempt `/api/auth/refresh` once per route and
 *   then fall back to `/api/auth/login`.
 *
 * @param to Destination route that is being entered.
 * @returns Redirect navigation to refresh/login when Shopware login is missing.
 */
export default defineNuxtRouteMiddleware(async (to) => {
    if (import.meta.server) return;

    const { redirectToLogin, removeRefreshAttempt } = useRedirectToLogin();
    const { isLoggedIn } = useUser();
    const refreshAttemptStateKey = 'require-login-shopware:refresh-attempted';

    if (isLoggedIn.value) {
        removeRefreshAttempt(refreshAttemptStateKey, to.fullPath);
        return;
    }

    const { ensureShopwareSession } = useEnsureShopwareSession();
    await ensureShopwareSession({ force: true, reason: `require-login-shopware:${to.path}` });

    if (isLoggedIn.value) {
        removeRefreshAttempt(refreshAttemptStateKey, to.fullPath);
        return;
    }

    return redirectToLogin({
        to,
        refreshAttemptStateKey
    });
});
